import type { CatalogEvent, CatalogMatchDetail, CatalogPlayer, ProjectState } from "@livcg/schema";
import { coachPlayerId, coachSide } from "@livcg/schema";
import { useEffect } from "react";
import { useFetch } from "../shared/useFetch.js";
import { sortByEventTime } from "./eventSort.js";

export function useMatchDetail(state: ProjectState) {
  const fetched = useFetch<CatalogMatchDetail>(`/api/catalog/matches/${state.matchId}`);
  const detail = fetched.data;
  const { refetch } = fetched;
  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch on a PFL resync only
  useEffect(() => {
    if (state.catalogRev) refetch();
  }, [state.catalogRev]);

  const allPlayers: CatalogPlayer[] = [
    ...(detail?.home.players ?? []),
    ...(detail?.away.players ?? []),
  ];
  /** The coach as a pseudo-player (no number) for card events; named from
   *  the crew, "Murabbiy" if the crew has no name yet. */
  const coach = (side: "home" | "away"): CatalogPlayer => {
    const c = state.crew?.[side === "home" ? "coachHome" : "coachAway"];
    const firstName = c?.firstName?.trim() ?? "";
    const lastName = c?.lastName?.trim() ?? "";
    return {
      id: coachPlayerId(side),
      firstName,
      lastName: lastName || (firstName ? "" : "Murabbiy"),
      number: null,
    };
  };
  const playerName = (id: string): string => {
    const cs = coachSide(id);
    if (cs) {
      const c = coach(cs);
      const full = `${c.firstName} ${c.lastName}`.trim();
      return full === "Murabbiy" ? full : `Murabbiy · ${full}`;
    }
    const p = allPlayers.find((x) => x.id === id);
    return p ? `${p.number ? `#${p.number} ` : ""}${p.firstName} ${p.lastName}` : id;
  };
  const teamShort = (id: string): string => {
    if (detail?.home.team.id === id) return detail.home.team.shortName ?? "—";
    if (detail?.away.team.id === id) return detail.away.team.shortName ?? "—";
    return "—";
  };
  /** `withCoach` — card events can also belong to the team's coach. */
  const teamPlayers = (teamId: string, withCoach = false): CatalogPlayer[] => {
    const side =
      detail?.home.team.id === teamId ? "home" : detail?.away.team.id === teamId ? "away" : null;
    if (!side || !detail) return [];
    return withCoach ? [...detail[side].players, coach(side)] : detail[side].players;
  };
  const sortedEvents: CatalogEvent[] = sortByEventTime(detail?.events ?? []);

  return {
    detail: detail ?? null,
    loading: fetched.loading,
    refetch: fetched.refetch,
    playerName,
    coach,
    teamShort,
    teamPlayers,
    sortedEvents,
  };
}
